/**
 * What a task has to be attached to, derived from the project it belongs to.
 *
 * `taskCNModel` requires **both** a `packageid` (the Location) and a
 * `projectid`, plus a `packageType`. Asking a field user to supply all three on
 * a phone would be absurd — so the project supplies the rest, which is already
 * how `CreateTaskCNModal` does it on the desktop.
 *
 * Extracted rather than copied, because two implementations of "which location
 * is this task really against" would drift, and the drift would produce tasks
 * filed under the wrong asset — visible only much later, on someone else's
 * screen.
 */

export interface TaskTarget {
	packageid: string
	packageType: string
	projectid: string
}

/**
 * `projectAsset` is the project's Location, and may arrive populated or as a
 * bare id depending on which endpoint returned the project. Both are handled,
 * because a caller should not have to know which one it has.
 */
export const taskTargetFromProject = (project: any): TaskTarget | null => {
	if (!project?._id) return null

	const asset = project.projectAsset
	const packageid = typeof asset === 'string' ? asset : asset?._id
	if (!packageid) return null

	return {
		packageid: String(packageid),
		// The desktop falls back to the project's own assetType, then to empty.
		// Empty is still sent: the field is required, and letting the server
		// reject it beats silently filing the task somewhere it does not belong.
		packageType: (typeof asset === 'object' ? asset?.assetType : '') || project.assetType || '',
		projectid: String(project._id),
	}
}

/**
 * A due date the API will accept.
 *
 * With a time, the local date and time are converted to UTC by the browser.
 * Without one, the noon-UTC sentinel is kept — the same value the desktop
 * writes, so a date-only task means the same thing wherever it was created.
 * Midnight would drift across a date boundary for anyone west of UTC, which is
 * everyone using this.
 */
export const taskDueIso = (date?: string, time?: string): string | undefined => {
	if (!date) return undefined
	if (!time) return `${date}T12:00:00.000Z`

	const parsed = new Date(`${date}T${time}:00`)
	return Number.isNaN(parsed.getTime()) ? `${date}T12:00:00.000Z` : parsed.toISOString()
}

/**
 * The value to put in a `<input type="date">` when EDITING a task.
 *
 * The inverse of `taskDueIso`, and it has to know which kind of date it is
 * looking at, because the two are stored differently:
 *
 *  - A date-only task holds the noon-UTC sentinel, so its day is the **UTC**
 *    day. Reading it back in local time shifts it for anyone far enough east:
 *    noon UTC is 1am the NEXT day in New Zealand.
 *  - A scheduled task holds a real instant, so its day is the **local** day,
 *    which is what the user picked and what the calendar already shows.
 *
 * Getting this backwards does not fail loudly. It moves a due date by a day
 * every time somebody opens a task and saves it unchanged.
 */
export const taskDueInputValue = (
	dateDue?: string | number | Date | null,
	hasScheduledTime?: boolean,
): string => {
	if (!dateDue) return ''
	const parsed = new Date(dateDue)
	if (Number.isNaN(parsed.getTime())) return ''
	if (hasScheduledTime) return localDayKey(parsed)
	return parsed.toISOString().slice(0, 10)
}

/** Local YYYY-MM-DD, for comparing a task's due date against a calendar cell. */
export const localDayKey = (value: string | number | Date): string => {
	const d = new Date(value)
	if (Number.isNaN(d.getTime())) return ''
	// Built from local parts, not toISOString, which would shift the day for
	// anyone behind UTC and put a task on the wrong square.
	const month = String(d.getMonth() + 1).padStart(2, '0')
	const day = String(d.getDate()).padStart(2, '0')
	return `${d.getFullYear()}-${month}-${day}`
}
